import {
  buildCrosswordBlankAnswer,
  normalizeCrosswordDirection,
  type CrosswordDirection,
} from './crosswordGrid';
import { generateWordSearchGrid } from './wordSearchGrid';

export function stripOptionHtml(html: string | undefined | null): string {
  if (!html) return '';
  if (typeof document === 'undefined') {
    return html.replace(/<[^>]*>/g, ' ').replace(/\s+/g, ' ').trim();
  }
  const temp = document.createElement('div');
  temp.innerHTML = html;
  return (temp.textContent || temp.innerText || '').replace(/\s+/g, ' ').trim();
}

export function getOptionPlainText(opt: any, fallback = ''): string {
  if (!opt) return fallback;
  if (opt.entryWord && String(opt.entryWord).trim()) return String(opt.entryWord).trim();
  const block = opt.blocks?.find((b: any) => b?.textContent);
  if (block?.textContent) return stripOptionHtml(block.textContent) || fallback;
  if (opt.textContent) return stripOptionHtml(opt.textContent) || fallback;
  return fallback;
}

export function parseFoundIds(answer: string | undefined): number[] {
  if (!answer?.trim()) return [];
  return answer
    .split(',')
    .map((t) => parseInt(t.trim(), 10))
    .filter((n) => !Number.isNaN(n));
}

export function getFindTheWordsWords(options: any[] | undefined): Array<{ id: number; word: string }> {
  return (options ?? [])
    .map((opt) => ({
      id: opt.id as number,
      word: getOptionPlainText(opt, ''),
    }))
    .filter((w) => typeof w.id === 'number' && w.word.length > 0);
}

/** Join direction labels: EN "a, b, or c" / FA "a، b یا c". */
export function formatFindTheWordsDirectionList(items: string[], isFa: boolean): string {
  if (items.length === 0) return '';
  if (items.length === 1) return items[0];
  const orWord = isFa ? ' یا ' : ' or ';
  const sep = isFa ? '، ' : ', ';
  if (items.length === 2) return `${items[0]}${orWord}${items[1]}`;
  return `${items.slice(0, -1).join(sep)}${isFa ? '' : ','}${orWord}${items[items.length - 1]}`;
}

type FindTheWordsDirectionFlags = {
  allowHorizontal?: boolean;
  allowVertical?: boolean;
  allowDiagonal?: boolean;
};

type TranslateFn = (key: string, opts?: Record<string, unknown>) => string;

/**
 * Builds the find-the-words learner hint from admin direction flags.
 * Defaults: horizontal + vertical on; diagonal only when explicitly true.
 */
export function buildFindTheWordsHint(
  q: FindTheWordsDirectionFlags,
  t: TranslateFn,
  isFa: boolean
): string {
  const dirs: string[] = [];
  if (q.allowHorizontal !== false) {
    dirs.push(
      t('interactiveQuestions.findTheWordsDirHorizontal', { defaultValue: 'horizontal' })
    );
  }
  if (q.allowVertical !== false) {
    dirs.push(t('interactiveQuestions.findTheWordsDirVertical', { defaultValue: 'vertical' }));
  }
  if (q.allowDiagonal === true) {
    dirs.push(t('interactiveQuestions.findTheWordsDirDiagonal', { defaultValue: 'diagonal' }));
  }

  if (dirs.length === 0) {
    return t('interactiveQuestions.findTheWordsHint', {
      defaultValue: 'Drag across letters in a straight line to find each word.',
    });
  }

  const directions = formatFindTheWordsDirectionList(dirs, isFa);
  return t('interactiveQuestions.findTheWordsHintWithDirections', {
    directions,
    defaultValue: `Drag across letters in a straight line (${directions}) to find each word.`,
  });
}

export function resolveFindTheWordsGrid(question: any): {
  gridLetters: string;
  rows: number;
  cols: number;
} {
  const words = getFindTheWordsWords(question?.questionOptions);
  let rows = Number(question?.gridRows) || 0;
  let cols = Number(question?.gridCols) || 0;
  let gridLetters = String(question?.gridLetters ?? '');

  if ((!gridLetters || !rows || !cols) && words.length > 0) {
    const longest = Math.max(...words.map((w) => w.word.length), 5);
    rows = rows || Math.max(8, longest + 2);
    cols = cols || Math.max(8, longest + 2);
    const generated = generateWordSearchGrid(
      words.map((w) => w.word),
      rows,
      cols,
      {
        horizontal: question?.allowHorizontal !== false,
        vertical: question?.allowVertical !== false,
        diagonal: question?.allowDiagonal === true,
        seed: typeof question?.id === 'number' ? question.id : 1,
      }
    );
    gridLetters = generated.gridLetters;
  }

  if (!rows || !cols) {
    const len = gridLetters.length || 1;
    rows = rows || Math.max(1, Math.floor(Math.sqrt(len)));
    cols = cols || Math.max(1, Math.ceil(len / rows));
  }

  return { gridLetters, rows, cols };
}

export function getCrosswordEntries(options: any[] | undefined): Array<{
  id: number;
  entryWord: string;
  clue: string;
  startRow: number;
  startCol: number;
  direction: CrosswordDirection;
}> {
  return (options ?? [])
    .map((opt, index) => {
      const entryWordRaw = opt.entryWord ?? opt.EntryWord;
      const entryWord =
        (typeof entryWordRaw === 'string' ? entryWordRaw.trim() : '') ||
        getOptionPlainText(opt, '');
      const startRowRaw = opt.startRow ?? opt.StartRow;
      const startColRaw = opt.startCol ?? opt.StartCol;
      const startRow = Number(startRowRaw);
      const startCol = Number(startColRaw);
      const rawDir = String(
        opt.wordDirection ?? opt.WordDirection ?? opt.direction ?? 'ltr'
      ).toLowerCase();
      const direction = normalizeCrosswordDirection(rawDir);
      const clueBlock =
        opt.blocks?.find((b: any) => b?.textContent)?.textContent ??
        opt.textContent ??
        opt.TextContent;
      const idRaw = opt.id ?? opt.Id;
      const id =
        typeof idRaw === 'number' && !Number.isNaN(idRaw)
          ? idRaw
          : idRaw != null && String(idRaw).trim() !== '' && !Number.isNaN(Number(idRaw))
            ? Number(idRaw)
            : index + 1;
      return {
        id,
        entryWord,
        clue: stripOptionHtml(clueBlock) || entryWord,
        startRow: Number.isFinite(startRow) ? startRow : 0,
        startCol: Number.isFinite(startCol) ? startCol : 0,
        direction,
      };
    })
    .filter((e) => e.entryWord.length > 0);
}

export function initialCrosswordAnswer(question: any): string {
  const rows = Number(question?.gridRows) || 0;
  const cols = Number(question?.gridCols) || 0;
  const entries = getCrosswordEntries(question?.questionOptions).map((e) => ({
    word: e.entryWord,
    startRow: e.startRow,
    startCol: e.startCol,
    direction: e.direction,
  }));
  if (rows <= 0 || cols <= 0 || entries.length === 0) return '';
  return buildCrosswordBlankAnswer(rows, cols, entries);
}

export function initialOrderFromOptions(options: any[] | undefined): number[] {
  return [...(options ?? [])]
    .sort((a, b) => (a.displayOrder ?? 0) - (b.displayOrder ?? 0))
    .map((opt) => opt.id ?? 0)
    .filter((id) => typeof id === 'number' && id > 0);
}
